import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import QRCode from 'qrcode';
import {
  PosterPalette,
  PosterFontChoices,
  PosterBackground,
  PosterOrnaments,
  PosterMascots,
  PosterMascotPosition,
  PosterMascotSize,
  PatternSettings,
  getPosterFont,
  getMascotScale,
  resolvePalette,
  ensurePosterFontsLoaded,
  DEFAULT_POSTER_MESSAGE,
} from '../../lib/posterDesign';
// Bundled rather than pulled from the asset CDN: the CDN serves no CORS
// headers, so a crossOrigin image from it fails to load — and dropping
// crossOrigin would taint the canvas and break PNG/PDF export entirely.
import logoSrc from '../../assets/poster/pixelmachha-logo.png';
import mascotSrc from '../../assets/poster/machha-photographer.png';
import welcomeMascotSrc from '../../assets/poster/machha-welcome.png';
// The wordmark ships as two fixed-colour artworks (LogoImages/blackfont and
// whitefont) — pick the one that reads against the poster's background.
import wordmarkOnLightSrc from '../../assets/poster/wordmark-on-light.png';
import wordmarkOnDarkSrc from '../../assets/poster/wordmark-on-dark.png';
import styles from './A4Poster.module.css';

// ── Geometry ──────────────────────────────────────────────────────────────────
// The layout is authored once in these base units and scaled on export, so the
// on-screen preview and the 300 DPI print file are the same drawing.
// 1240 x 1754 is A4's 1:√2 at ~150 DPI.
export const BASE_W = 1240;
export const BASE_H = 1754;

/** Scale factor that turns the base layout into a 300 DPI (2480x3508) page. */
export const PRINT_SCALE = 2;

const MARGIN = 92;

export interface A4PosterContent {
  eventName: string;
  albumName: string;
  inviteUrl: string;
  message: string;
  /** Print the invite URL under the QR as a fallback for failed scans. */
  showUrl: boolean;
  fonts: PosterFontChoices;
  /** Background gradient endpoints; the ink set is derived from them. */
  bgPrimary: string;
  bgSecondary: string;
  background: PosterBackground;
  patternSettings: PatternSettings;
  ornaments: PosterOrnaments;
  ornamentCount: number;
  /** Bumped by the Randomise button to reshuffle ornament placement. */
  ornamentSeed: number;
  mascots: PosterMascots;
  mascotPosition: PosterMascotPosition;
  mascotSize: PosterMascotSize;
}

export interface A4PosterHandle {
  /**
   * Render a fresh canvas at print resolution. The preview canvas is
   * deliberately smaller, so exports re-draw rather than upscale.
   */
  renderPrintCanvas: () => Promise<HTMLCanvasElement>;
}

interface PosterAssets {
  logo: HTMLImageElement | null;
  /** Wordmark artwork already matched to the poster's colour mode. */
  wordmark: HTMLImageElement | null;
  mascot: HTMLImageElement | null;
  welcomeMascot: HTMLImageElement | null;
  qr: HTMLImageElement;
}

async function loadPosterAssets(content: A4PosterContent): Promise<PosterAssets> {
  const palette = resolvePalette(content.bgPrimary, content.bgSecondary);
  // The wordmark ships as fixed-colour artwork, so pick the variant that
  // contrasts with whatever background colours were chosen.
  const darkBackground = palette.textPrimary === '#ffffff';
  const [logo, wordmark, mascot, welcomeMascot, qrDataUrl] = await Promise.all([
    loadImage(logoSrc).catch(() => null),
    loadImage(darkBackground ? wordmarkOnDarkSrc : wordmarkOnLightSrc).catch(() => null),
    loadImage(mascotSrc).catch(() => null),
    loadImage(welcomeMascotSrc).catch(() => null),
    QRCode.toDataURL(content.inviteUrl, {
      // Generated well above its printed size so scaling never softens the
      // module edges. 'Q' keeps it readable if the print gets scuffed.
      width: 1400,
      margin: 0,
      color: { dark: palette.qrFg, light: palette.qrCardBg },
      errorCorrectionLevel: 'Q',
    }),
  ]);

  return { logo, wordmark, mascot, welcomeMascot, qr: await loadImage(qrDataUrl) };
}

// ── Clay primitives ───────────────────────────────────────────────────────────

/**
 * Draw a claymorphic surface: soft outer drop shadow, a subtly inflated fill,
 * and inner rims (light along the top, dark along the bottom). Canvas has no
 * `inset` shadow, so the rims are clipped gradients.
 */
function clayPanel(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
  p: PosterPalette,
  opts: { fill?: string; elevation?: number; rim?: number } = {},
) {
  const fill = opts.fill ?? p.surface;
  const elevation = opts.elevation ?? 1;
  const rim = opts.rim ?? Math.max(3, Math.min(h, w) * 0.05);

  // Outer drop shadow
  ctx.save();
  ctx.shadowColor = p.shadow;
  ctx.shadowBlur = 34 * elevation;
  ctx.shadowOffsetY = 14 * elevation;
  ctx.fillStyle = fill;
  roundRect(ctx, x, y, w, h, r);
  ctx.fill();
  ctx.restore();

  // Inner rims, clipped to the surface
  ctx.save();
  roundRect(ctx, x, y, w, h, r);
  ctx.clip();

  const top = ctx.createLinearGradient(0, y, 0, y + rim);
  top.addColorStop(0, p.surfaceHighlight);
  top.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = top;
  ctx.fillRect(x, y, w, rim);

  const bottom = ctx.createLinearGradient(0, y + h - rim, 0, y + h);
  bottom.addColorStop(0, 'rgba(0,0,0,0)');
  bottom.addColorStop(1, p.surfaceShade);
  ctx.fillStyle = bottom;
  ctx.fillRect(x, y + h - rim, w, rim);

  ctx.restore();
}

/**
 * The inverse of `clayPanel` — a pressed/recessed field. Used for the invite
 * URL, which reads as something written into the poster rather than on it.
 */
function clayPressed(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
  p: PosterPalette,
) {
  ctx.fillStyle = p.surfaceSunken;
  roundRect(ctx, x, y, w, h, r);
  ctx.fill();

  ctx.save();
  roundRect(ctx, x, y, w, h, r);
  ctx.clip();

  const rim = h * 0.28;
  const top = ctx.createLinearGradient(0, y, 0, y + rim);
  top.addColorStop(0, p.surfaceShade);
  top.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = top;
  ctx.fillRect(x, y, w, rim);

  const bottom = ctx.createLinearGradient(0, y + h - rim, 0, y + h);
  bottom.addColorStop(0, 'rgba(255,255,255,0)');
  bottom.addColorStop(1, p.surfaceHighlight);
  ctx.fillStyle = bottom;
  ctx.fillRect(x, y + h - rim, w, rim);

  ctx.restore();

  ctx.strokeStyle = hexToRgba(p.accent, 0.28);
  ctx.lineWidth = 2;
  roundRect(ctx, x + 1, y + 1, w - 2, h - 2, r - 1);
  ctx.stroke();
}

// ── Background patterns ───────────────────────────────────────────────────────

/**
 * Deterministic PRNG (mulberry32). Scattered layouts must be identical between
 * the preview draw and the print draw, so they can't use Math.random().
 */
function seeded(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Paint the chosen pattern across the page, beneath all content.
 *
 * `k` holds the pattern's knob values as multipliers around 1, so each pattern
 * applies them to whatever geometry it owns (spacing, stroke width, count).
 */
function drawBackgroundPattern(
  ctx: CanvasRenderingContext2D,
  pattern: PosterBackground,
  k: PatternSettings,
  p: PosterPalette,
) {
  if (pattern === 'plain') return;

  const scale = k.scale;
  const thick = k.thickness;
  const density = k.density;
  const rad = (k.angle * Math.PI) / 180;

  if (pattern === 'glow') {
    for (const blob of [
      { x: BASE_W * 0.95, y: BASE_H * 0.05, r: 300 },
      { x: BASE_W * 0.02, y: BASE_H * 0.34, r: 250 },
      { x: BASE_W * 0.92, y: BASE_H * 0.72, r: 270 },
      { x: BASE_W * 0.3, y: BASE_H * 1.0, r: 220 },
    ]) {
      const r = blob.r * scale;
      const g = ctx.createRadialGradient(blob.x, blob.y, 0, blob.x, blob.y, r);
      g.addColorStop(0, withAlphaScale(p.blob, k.opacity));
      g.addColorStop(1, 'rgba(239, 68, 68, 0)');
      ctx.fillStyle = g;
      ctx.fillRect(blob.x - r, blob.y - r, r * 2, r * 2);
    }
    return;
  }

  if (pattern === 'mesh') {
    // Overlapping wide radial fields — a soft, abstract colour wash.
    const rand = seeded(4471);
    for (let i = 0; i < 7; i++) {
      const x = rand() * BASE_W;
      const y = rand() * BASE_H;
      const r = (320 + rand() * 320) * scale;
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, withAlphaScale(p.blob, k.opacity * 1.1));
      g.addColorStop(1, 'rgba(239, 68, 68, 0)');
      ctx.fillStyle = g;
      ctx.fillRect(x - r, y - r, r * 2, r * 2);
    }
    return;
  }

  ctx.save();
  const ink = withAlphaScale(p.pattern, k.opacity);
  ctx.fillStyle = ink;
  ctx.strokeStyle = ink;

  switch (pattern) {
    case 'dots': {
      const step = 58 * scale;
      for (let y = step / 2; y < BASE_H; y += step) {
        for (let x = step / 2; x < BASE_W; x += step) {
          ctx.beginPath();
          ctx.arc(x, y, 5 * thick, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      break;
    }
    case 'halftone': {
      // Dot size ramps along the angle axis, giving a printed-gradient feel.
      const step = 46 * scale;
      const ux = Math.cos(rad);
      const uy = Math.sin(rad);
      const span = Math.abs(BASE_W * ux) + Math.abs(BASE_H * uy) || 1;
      for (let y = step / 2; y < BASE_H; y += step) {
        for (let x = step / 2; x < BASE_W; x += step) {
          const t = Math.min(1, Math.max(0, (x * ux + y * uy) / span));
          ctx.beginPath();
          ctx.arc(x, y, 0.8 + t * 7 * thick, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      break;
    }
    case 'grid': {
      const step = 68 * scale;
      ctx.lineWidth = 2 * thick;
      ctx.save();
      ctx.translate(BASE_W / 2, BASE_H / 2);
      ctx.rotate(rad - Math.PI / 4);
      ctx.translate(-BASE_W / 2, -BASE_H / 2);
      const over = BASE_H;
      ctx.beginPath();
      for (let x = -over; x <= BASE_W + over; x += step) {
        ctx.moveTo(x, -over);
        ctx.lineTo(x, BASE_H + over);
      }
      for (let y = -over; y <= BASE_H + over; y += step) {
        ctx.moveTo(-over, y);
        ctx.lineTo(BASE_W + over, y);
      }
      ctx.stroke();
      ctx.restore();
      break;
    }
    case 'crosshatch': {
      const step = 40 * scale;
      ctx.lineWidth = 1.6 * thick;
      for (const dir of [rad, rad + Math.PI / 2]) {
        const dx = Math.cos(dir);
        const dy = Math.sin(dir);
        const len = BASE_W + BASE_H;
        ctx.beginPath();
        for (let i = -len; i < len; i += step) {
          ctx.moveTo(-dy * i - dx * len, dx * i - dy * len);
          ctx.lineTo(-dy * i + dx * len, dx * i + dy * len);
        }
        ctx.stroke();
      }
      break;
    }
    case 'diagonal': {
      const step = 54 * scale;
      ctx.lineWidth = 14 * thick;
      ctx.save();
      ctx.translate(BASE_W / 2, BASE_H / 2);
      ctx.rotate(rad - Math.PI / 4);
      ctx.translate(-BASE_W / 2, -BASE_H / 2);
      const over = BASE_H;
      ctx.beginPath();
      for (let i = -over; i < BASE_W + over; i += step) {
        ctx.moveTo(i, -over);
        ctx.lineTo(i + BASE_H, BASE_H + over);
      }
      ctx.stroke();
      ctx.restore();
      break;
    }
    case 'chevron': {
      const step = 62 * scale;
      const w = 46 * scale;
      ctx.lineWidth = 5 * thick;
      ctx.lineJoin = 'round';
      for (let y = -step; y < BASE_H + step; y += step) {
        ctx.beginPath();
        for (let x = -w; x < BASE_W + w * 2; x += w * 2) {
          ctx.moveTo(x, y + step / 3);
          ctx.lineTo(x + w, y - step / 3);
          ctx.lineTo(x + w * 2, y + step / 3);
        }
        ctx.stroke();
      }
      break;
    }
    case 'waves': {
      ctx.lineWidth = 4 * thick;
      const amp = 26 * scale;
      for (let y = 90; y < BASE_H; y += 86 * scale) {
        ctx.beginPath();
        for (let x = 0; x <= BASE_W; x += 8) {
          const wy = y + Math.sin((x / BASE_W) * Math.PI * 5) * amp;
          if (x === 0) ctx.moveTo(x, wy);
          else ctx.lineTo(x, wy);
        }
        ctx.stroke();
      }
      break;
    }
    case 'topography': {
      // Nested wobbling closed curves — contour-map feel without any noise lib.
      ctx.lineWidth = 2.4 * thick;
      const rand = seeded(90210);
      for (const centre of [
        { x: BASE_W * 0.2, y: BASE_H * 0.22, p: rand() * 6 },
        { x: BASE_W * 0.82, y: BASE_H * 0.55, p: rand() * 6 },
        { x: BASE_W * 0.35, y: BASE_H * 0.86, p: rand() * 6 },
      ]) {
        for (let ring = 1; ring <= 9; ring++) {
          const base = ring * 46 * scale;
          ctx.beginPath();
          for (let a = 0; a <= Math.PI * 2 + 0.01; a += 0.08) {
            const wobble = Math.sin(a * 3 + centre.p) * 14 + Math.sin(a * 5 - centre.p) * 8;
            const r = base + wobble * scale;
            const x = centre.x + Math.cos(a) * r;
            const y = centre.y + Math.sin(a) * r * 0.82;
            if (a === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          }
          ctx.closePath();
          ctx.stroke();
        }
      }
      break;
    }
    case 'arcs': {
      const step = 92 * scale;
      ctx.lineWidth = 4 * thick;
      for (let y = 0; y < BASE_H + step; y += step) {
        for (let x = 0; x < BASE_W + step; x += step) {
          ctx.beginPath();
          ctx.arc(x, y, step / 2, rad, rad + Math.PI / 2);
          ctx.stroke();
        }
      }
      break;
    }
    case 'rings': {
      ctx.lineWidth = 3 * thick;
      for (const centre of [
        { x: BASE_W * 0.14, y: BASE_H * 0.14 },
        { x: BASE_W * 0.88, y: BASE_H * 0.62 },
      ]) {
        for (let r = 70 * scale; r < 620; r += 62 * scale) {
          ctx.beginPath();
          ctx.arc(centre.x, centre.y, r, 0, Math.PI * 2);
          ctx.stroke();
        }
      }
      break;
    }
    case 'confetti': {
      const rand = seeded(20260829);
      const n = Math.round(150 * density);
      for (let i = 0; i < n; i++) {
        const x = rand() * BASE_W;
        const y = rand() * BASE_H;
        const w = (10 + rand() * 16) * thick;
        const h = (5 + rand() * 8) * thick;
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(rand() * Math.PI);
        ctx.globalAlpha = 0.55 + rand() * 0.45;
        roundRect(ctx, -w / 2, -h / 2, w, h, h / 2);
        ctx.fill();
        ctx.restore();
      }
      break;
    }
    case 'scatter': {
      const rand = seeded(551);
      const n = Math.round(70 * density);
      for (let i = 0; i < n; i++) {
        const x = rand() * BASE_W;
        const y = rand() * BASE_H;
        const w = (26 + rand() * 46) * thick;
        const h = 9 * thick;
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(rad + (rand() - 0.5) * 0.5);
        ctx.globalAlpha = 0.5 + rand() * 0.5;
        roundRect(ctx, -w / 2, -h / 2, w, h, h / 2);
        ctx.fill();
        ctx.restore();
      }
      break;
    }
    case 'terrazzo': {
      const rand = seeded(31337);
      const n = Math.round(120 * density);
      for (let i = 0; i < n; i++) {
        const x = rand() * BASE_W;
        const y = rand() * BASE_H;
        const r = (5 + rand() * 12) * thick;
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(rand() * Math.PI * 2);
        ctx.globalAlpha = 0.45 + rand() * 0.55;
        ctx.beginPath();
        const sides = 5 + Math.floor(rand() * 3);
        for (let s = 0; s < sides; s++) {
          const a = (s / sides) * Math.PI * 2;
          const rr = r * (0.65 + rand() * 0.6);
          const px = Math.cos(a) * rr;
          const py = Math.sin(a) * rr;
          if (s === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      }
      break;
    }
    case 'triangles': {
      const rand = seeded(8642);
      const n = Math.round(55 * density);
      for (let i = 0; i < n; i++) {
        const x = rand() * BASE_W;
        const y = rand() * BASE_H;
        const r = (14 + rand() * 30) * thick;
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(rand() * Math.PI * 2);
        ctx.globalAlpha = 0.4 + rand() * 0.6;
        ctx.beginPath();
        ctx.moveTo(0, -r);
        ctx.lineTo(r * 0.87, r * 0.5);
        ctx.lineTo(-r * 0.87, r * 0.5);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      }
      break;
    }
    case 'bokeh': {
      const rand = seeded(7318);
      const n = Math.round(40 * density);
      for (let i = 0; i < n; i++) {
        const x = rand() * BASE_W;
        const y = rand() * BASE_H;
        const r = (18 + rand() * 74) * thick;
        ctx.globalAlpha = 0.25 + rand() * 0.5;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
  }

  ctx.restore();
}

/**
 * A clay orb — an inflated sphere lit from the top-left. Used for the
 * decorative layer, drawn rather than imported so every ornament stays inside
 * the red/black/white palette.
 */
function clayOrb(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, r: number,
  base: string,
  p: PosterPalette,
) {
  ctx.save();
  ctx.shadowColor = p.shadow;
  ctx.shadowBlur = r * 0.7;
  ctx.shadowOffsetY = r * 0.28;

  const g = ctx.createRadialGradient(cx - r * 0.35, cy - r * 0.4, r * 0.1, cx, cy, r);
  g.addColorStop(0, hexToRgba('#ffffff', 0.55));
  g.addColorStop(0.35, base);
  g.addColorStop(1, base);
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // Specular highlight
  ctx.save();
  ctx.globalAlpha = 0.5;
  const h = ctx.createRadialGradient(
    cx - r * 0.36, cy - r * 0.42, 0,
    cx - r * 0.36, cy - r * 0.42, r * 0.42,
  );
  h.addColorStop(0, 'rgba(255,255,255,0.9)');
  h.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = h;
  ctx.beginPath();
  ctx.arc(cx - r * 0.36, cy - r * 0.42, r * 0.42, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/**
 * A four-pointed clay sparkle. Same idea as {@link clayOrb} — the shipped
 * sparkle artwork is pastel multi-colour, which the brand palette rules out.
 */
function claySparkle(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, r: number,
  fill: string,
  p: PosterPalette,
) {
  clayShadowed(ctx, r, p, () => {
    ctx.fillStyle = fill;
    ctx.beginPath();
    ctx.moveTo(cx, cy - r);
    ctx.quadraticCurveTo(cx + r * 0.16, cy - r * 0.16, cx + r, cy);
    ctx.quadraticCurveTo(cx + r * 0.16, cy + r * 0.16, cx, cy + r);
    ctx.quadraticCurveTo(cx - r * 0.16, cy + r * 0.16, cx - r, cy);
    ctx.quadraticCurveTo(cx - r * 0.16, cy - r * 0.16, cx, cy - r);
    ctx.closePath();
    ctx.fill();
  });
}

/** Shared clay drop shadow wrapper for the flat ornament shapes. */
function clayShadowed(
  ctx: CanvasRenderingContext2D,
  r: number,
  p: PosterPalette,
  draw: () => void,
) {
  ctx.save();
  ctx.shadowColor = p.shadow;
  ctx.shadowBlur = r * 0.6;
  ctx.shadowOffsetY = r * 0.22;
  draw();
  ctx.restore();
}

function clayRing(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, r: number,
  fill: string,
  p: PosterPalette,
) {
  clayShadowed(ctx, r, p, () => {
    ctx.strokeStyle = fill;
    ctx.lineWidth = Math.max(4, r * 0.34);
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.78, 0, Math.PI * 2);
    ctx.stroke();
  });
}

function clayPill(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, r: number,
  fill: string,
  p: PosterPalette,
  rotation: number,
) {
  clayShadowed(ctx, r, p, () => {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(rotation);
    ctx.fillStyle = fill;
    const w = r * 2.4;
    const h = r * 0.95;
    roundRect(ctx, -w / 2, -h / 2, w, h, h / 2);
    ctx.fill();
    ctx.restore();
  });
}

function claySquare(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, r: number,
  fill: string,
  p: PosterPalette,
  rotation: number,
) {
  clayShadowed(ctx, r, p, () => {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(rotation);
    ctx.fillStyle = fill;
    roundRect(ctx, -r, -r, r * 2, r * 2, r * 0.42);
    ctx.fill();
    ctx.restore();
  });
}

function clayTriangle(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, r: number,
  fill: string,
  p: PosterPalette,
  rotation: number,
) {
  clayShadowed(ctx, r, p, () => {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(rotation);
    ctx.fillStyle = fill;
    ctx.lineJoin = 'round';
    ctx.lineWidth = r * 0.3;
    ctx.strokeStyle = fill;
    ctx.beginPath();
    ctx.moveTo(0, -r);
    ctx.lineTo(r * 0.87, r * 0.5);
    ctx.lineTo(-r * 0.87, r * 0.5);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  });
}

function clayDiamond(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, r: number,
  fill: string,
  p: PosterPalette,
  rotation: number,
) {
  clayShadowed(ctx, r, p, () => {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(rotation);
    ctx.fillStyle = fill;
    ctx.lineJoin = 'round';
    ctx.lineWidth = r * 0.3;
    ctx.strokeStyle = fill;
    ctx.beginPath();
    ctx.moveTo(0, -r);
    ctx.lineTo(r * 0.7, 0);
    ctx.lineTo(0, r);
    ctx.lineTo(-r * 0.7, 0);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  });
}

const ORNAMENT_SHAPES = [
  'orbs', 'sparkles', 'rings', 'pills', 'squares', 'triangles', 'diamonds',
] as const;
type OrnamentShape = (typeof ORNAMENT_SHAPES)[number];

/**
 * Scatter clay ornaments around the page.
 *
 * Positions come from `seed`, so the Randomise button just bumps the seed and
 * the print export reproduces exactly what the preview showed. Placement is
 * confined to the left and right margins plus the top band: the centre column
 * carries the QR and text and must stay clear.
 */
function drawOrnaments(
  ctx: CanvasRenderingContext2D,
  style: PosterOrnaments,
  count: number,
  seed: number,
  p: PosterPalette,
) {
  if (style === 'none' || count <= 0) return;

  const rand = seeded(seed || 1);
  const shapes: OrnamentShape[] =
    style === 'mixed' ? [...ORNAMENT_SHAPES] : [style as OrnamentShape];

  const sideW = 180;
  for (let i = 0; i < count; i++) {
    // Alternate sides so ornaments stay balanced at any count.
    const onLeft = i % 2 === 0;
    const x = onLeft
      ? rand() * sideW - 20
      : BASE_W - sideW + rand() * sideW + 20;
    const y = 120 + rand() * (BASE_H - 260);
    const r = 18 + rand() * 34;
    const shape = shapes[Math.floor(rand() * shapes.length)];
    // Roughly a third pick up the brand red; the rest are neutral clay.
    const fill = rand() < 0.34 ? hexToRgba(p.accent, 0.88) : p.ornament;
    const rotation = rand() * Math.PI * 2;

    switch (shape) {
      case 'orbs': clayOrb(ctx, x, y, r, fill, p); break;
      case 'sparkles': claySparkle(ctx, x, y, r * 0.8, fill, p); break;
      case 'rings': clayRing(ctx, x, y, r, fill, p); break;
      case 'pills': clayPill(ctx, x, y, r * 0.8, fill, p, rotation); break;
      case 'squares': claySquare(ctx, x, y, r * 0.8, fill, p, rotation); break;
      case 'triangles': clayTriangle(ctx, x, y, r * 0.9, fill, p, rotation); break;
      case 'diamonds': clayDiamond(ctx, x, y, r * 0.95, fill, p, rotation); break;
    }
  }
}

// ── Drawing ───────────────────────────────────────────────────────────────────

/**
 * Draw the full poster into `ctx` at `scale`. Assets are passed in so the
 * preview and print passes share one fetch.
 */
export function drawA4Poster(
  ctx: CanvasRenderingContext2D,
  content: A4PosterContent,
  assets: PosterAssets,
  scale: number,
) {
  const p = resolvePalette(content.bgPrimary, content.bgSecondary);
  const font = (slot: keyof PosterFontChoices, weight: number, size: number) => {
    const f = getPosterFont(content.fonts[slot]);
    return `${weight} ${Math.round(size * (f.scale ?? 1))}px ${f.stack}`;
  };

  ctx.save();
  ctx.scale(scale, scale);

  // ── Background ──────────────────────────────────────────────────────────
  const bg = ctx.createLinearGradient(0, 0, BASE_W * 0.3, BASE_H);
  bg.addColorStop(0, p.bgFrom);
  bg.addColorStop(1, p.bgTo);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, BASE_W, BASE_H);

  drawBackgroundPattern(ctx, content.background, content.patternSettings, p);

  // Clay ornaments — solid, inflated shapes that give the page the same
  // tactile read as the panels. Confined to the margins so they never crowd
  // the QR or compete with the mascots.
  drawOrnaments(ctx, content.ornaments, content.ornamentCount, content.ornamentSeed, p);

  // ── Header: brand lockup — icon on a clay chip + wordmark artwork ───────
  // "Pixel Machha" is never set as type; it is always the wordmark PNG.
  const chip = 112;
  const chipRadius = 34;
  const headerGap = 28;
  const wordmarkH = 54;
  const wordmarkW = assets.wordmark
    ? (assets.wordmark.naturalWidth / assets.wordmark.naturalHeight) * wordmarkH
    : 0;

  const headerW = chip + (wordmarkW ? headerGap + wordmarkW : 0);
  const headerX = (BASE_W - headerW) / 2;

  // The chip stays light in both modes so the black camera mark reads.
  clayPanel(ctx, headerX, MARGIN, chip, chip, chipRadius, p, {
    fill: p.logoChip,
    elevation: 1,
    rim: 16,
  });
  if (assets.logo) {
    const inset = 15;
    ctx.drawImage(assets.logo, headerX + inset, MARGIN + inset, chip - inset * 2, chip - inset * 2);
  }
  if (assets.wordmark) {
    ctx.drawImage(
      assets.wordmark,
      headerX + chip + headerGap,
      MARGIN + (chip - wordmarkH) / 2,
      wordmarkW,
      wordmarkH,
    );
  }

  const dividerY = MARGIN + chip + 44;
  ctx.strokeStyle = p.divider;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(MARGIN + 40, dividerY);
  ctx.lineTo(BASE_W - MARGIN - 40, dividerY);
  ctx.stroke();

  // ── Measure the body so it can be centred as one block ──────────────────
  const contentW = BASE_W - MARGIN * 2;

  ctx.font = font('eventName', 800, 60);
  const eventLines = wrapLines(ctx, content.eventName || 'Event', contentW, 2);

  ctx.font = font('message', 500, 33);
  const messageLines = wrapLines(
    ctx,
    content.message?.trim() || DEFAULT_POSTER_MESSAGE,
    contentW - 120,
    2,
  );

  ctx.font = font('albumName', 700, 44);
  const albumLines = wrapLines(ctx, content.albumName || '', contentW - 220, 2);

  const EVENT_LH = 74;
  const MSG_LH = 46;
  const ALBUM_LH = 56;
  const URL_H = 78;
  const ALBUM_LABEL_H = 32;
  const ALBUM_PAD_Y = 26;

  const gaps = { afterEvent: 38, afterMessage: 40, afterQr: 32, afterUrl: 40 };
  // With no mascot the bottom band collapses to just the footer, and the
  // reclaimed height flows into the QR.
  const mascotH = content.mascots === 'none' ? 96 : 380 * getMascotScale(content.mascotSize);
  const mascotTop = BASE_H - 74 - mascotH;
  const bandTop = dividerY + 44;
  const bandH = mascotTop - 20 - bandTop;

  const albumBlockH = albumLines.length
    ? ALBUM_LABEL_H + albumLines.length * ALBUM_LH + ALBUM_PAD_Y * 2
    : 0;

  const fixedH =
    eventLines.length * EVENT_LH +
    gaps.afterEvent +
    messageLines.length * MSG_LH +
    gaps.afterMessage +
    gaps.afterQr +
    (content.showUrl ? URL_H + gaps.afterUrl : 0) +
    albumBlockH;

  // The QR is the flexible element: it absorbs the slack so the block always
  // fills the band exactly, and shrinks when long names crowd it. The floor is
  // set so even the worst case (two-line event *and* album names) still fits
  // above the mascot — 360 base units is ~61mm on A4, scannable from metres.
  const qrPad = 42;
  const qrCard = clamp(bandH - fixedH, 360, 680);
  const qrSize = qrCard - qrPad * 2;

  let y = bandTop + Math.max(0, (bandH - (fixedH + qrCard)) / 2);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  const cx = BASE_W / 2;

  // ── Event name ──────────────────────────────────────────────────────────
  ctx.fillStyle = p.textPrimary;
  ctx.font = font('eventName', 800, 60);
  for (const line of eventLines) {
    ctx.fillText(line, cx, y);
    y += EVENT_LH;
  }
  y += gaps.afterEvent;

  // ── Message ─────────────────────────────────────────────────────────────
  ctx.fillStyle = p.textSecondary;
  ctx.font = font('message', 500, 33);
  for (const line of messageLines) {
    ctx.fillText(line, cx, y);
    y += MSG_LH;
  }
  y += gaps.afterMessage;

  // ── QR on a raised clay card ────────────────────────────────────────────
  const cardX = cx - qrCard / 2;
  clayPanel(ctx, cardX, y, qrCard, qrCard, 46, p, {
    fill: p.qrCardBg,
    elevation: 1.5,
    rim: 20,
  });
  ctx.drawImage(assets.qr, cardX + qrPad, y + qrPad, qrSize, qrSize);
  y += qrCard + gaps.afterQr;

  // ── Invite URL in a pressed clay field ──────────────────────────────────
  // Optional: it is the fallback for a scan that fails, so hiding it is a
  // deliberate choice. Always monospace — guests transcribe it by hand.
  if (content.showUrl) {
    ctx.font = `500 30px ui-monospace, "SF Mono", Menlo, Consolas, monospace`;
    const urlText = displayUrl(content.inviteUrl);
    const urlW = Math.min(ctx.measureText(urlText).width + 76, contentW);
    clayPressed(ctx, cx - urlW / 2, y, urlW, URL_H, URL_H / 2, p);

    ctx.fillStyle = p.textPrimary;
    ctx.textBaseline = 'middle';
    ctx.fillText(fitText(ctx, urlText, urlW - 52), cx, y + URL_H / 2 + 1);
    ctx.textBaseline = 'top';
    y += URL_H + gaps.afterUrl;
  }

  // ── Album name on a raised clay chip ────────────────────────────────────
  if (albumLines.length) {
    ctx.font = font('albumName', 700, 44);
    let widest = 0;
    for (const line of albumLines) widest = Math.max(widest, ctx.measureText(line).width);

    const chipW = Math.min(Math.max(widest + 120, 380), contentW);
    const chipH = albumBlockH;
    clayPanel(ctx, cx - chipW / 2, y, chipW, chipH, 34, p, { elevation: 1, rim: 12 });

    let ty = y + ALBUM_PAD_Y;
    setLetterSpacing(ctx, 5);
    ctx.fillStyle = p.accent;
    ctx.font = `700 20px "Inter", system-ui, sans-serif`;
    ctx.fillText('ALBUM', cx, ty);
    setLetterSpacing(ctx, 0);
    ty += ALBUM_LABEL_H;

    ctx.fillStyle = p.textPrimary;
    ctx.font = font('albumName', 700, 44);
    for (const line of albumLines) {
      ctx.fillText(line, cx, ty);
      ty += ALBUM_LH;
    }
  }

  // ── Mascots ─────────────────────────────────────────────────────────────
  // Each is measured first so that any position can lay them out as a group;
  // all variants keep their feet on the same baseline.
  const showPhotographer = content.mascots === 'both' || content.mascots === 'photographer';
  const showWelcome = content.mascots === 'both' || content.mascots === 'welcome';
  const mascotBaseline = mascotTop + mascotH;

  const drawn: { img: HTMLImageElement; w: number; h: number }[] = [];
  if (assets.mascot && showPhotographer) {
    const h = mascotH;
    drawn.push({
      img: assets.mascot,
      h,
      w: (assets.mascot.naturalWidth / assets.mascot.naturalHeight) * h,
    });
  }
  if (assets.welcomeMascot && showWelcome) {
    const h = mascotH * 0.86;
    drawn.push({
      img: assets.welcomeMascot,
      h,
      w: (assets.welcomeMascot.naturalWidth / assets.welcomeMascot.naturalHeight) * h,
    });
  }

  if (drawn.length) {
    const gap = 16;
    const groupW = drawn.reduce((sum, m) => sum + m.w, 0) + gap * (drawn.length - 1);

    if (content.mascotPosition === 'split' && drawn.length > 1) {
      // Spread to opposite corners rather than sitting as one block.
      const [first, ...rest] = drawn;
      ctx.drawImage(first.img, MARGIN - 20, mascotBaseline - first.h, first.w, first.h);
      let rightEdge = BASE_W - MARGIN + 20;
      for (const m of rest.reverse()) {
        ctx.drawImage(m.img, rightEdge - m.w, mascotBaseline - m.h, m.w, m.h);
        rightEdge -= m.w + gap;
      }
    } else {
      let x: number;
      switch (content.mascotPosition) {
        case 'right': x = BASE_W - MARGIN + 20 - groupW; break;
        case 'center': x = (BASE_W - groupW) / 2; break;
        // A lone mascot under 'split' falls back to the left corner.
        default: x = MARGIN - 20; break;
      }
      for (const m of drawn) {
        ctx.drawImage(m.img, x, mascotBaseline - m.h, m.w, m.h);
        x += m.w + gap;
      }
    }
  }

  // ── Footer ──────────────────────────────────────────────────────────────
  // Centred between the two mascots. "Pixel Machha" is the wordmark artwork
  // here as well — never type.
  const footerH = 28;
  const footerY = BASE_H - 62;
  const footerMarkW = assets.wordmark
    ? (assets.wordmark.naturalWidth / assets.wordmark.naturalHeight) * footerH
    : 0;
  ctx.font = `500 22px "Inter", system-ui, sans-serif`;
  const poweredW = ctx.measureText('Powered by').width;
  const footerTotal = poweredW + (footerMarkW ? 12 + footerMarkW : 0);
  const footerX = cx - footerTotal / 2;

  ctx.fillStyle = p.textMuted;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText('Powered by', footerX, footerY + footerH / 2);
  if (assets.wordmark) {
    ctx.drawImage(assets.wordmark, footerX + poweredW + 12, footerY, footerMarkW, footerH);
  }

  ctx.restore();
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * A4Poster — an A4-portrait guest poster drawn on canvas in the claymorphism
 * style (raised surfaces, soft shadows, inner rims; red + black + white only).
 *
 * The visible canvas renders at the base size for a responsive preview;
 * `renderPrintCanvas()` re-draws the same layout at 300 DPI for export.
 */
export const A4Poster = forwardRef<A4PosterHandle, { content: A4PosterContent }>(
  ({ content }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const assetsRef = useRef<PosterAssets | null>(null);
    // Tracking *what* was drawn (rather than a boolean) means a content change
    // marks the canvas stale without a synchronous setState in the effect.
    const [drawnFor, setDrawnFor] = useState<A4PosterContent | null>(null);
    const ready = drawnFor === content;

    useImperativeHandle(ref, () => ({
      renderPrintCanvas: async () => {
        const assets = assetsRef.current ?? (await loadPosterAssets(content));
        const canvas = document.createElement('canvas');
        canvas.width = BASE_W * PRINT_SCALE;
        canvas.height = BASE_H * PRINT_SCALE;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas is unavailable');
        drawA4Poster(ctx, content, assets, PRINT_SCALE);
        return canvas;
      },
    }));

    useEffect(() => {
      let cancelled = false;

      (async () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        await ensurePosterFontsLoaded(Object.values(content.fonts));
        const assets = await loadPosterAssets(content);
        if (cancelled) return;

        assetsRef.current = assets;
        canvas.width = BASE_W;
        canvas.height = BASE_H;
        drawA4Poster(ctx, content, assets, 1);
        setDrawnFor(content);
      })().catch(() => {
        // Leave the canvas in its loading state; the modal surfaces errors on
        // export rather than blocking the preview.
      });

      return () => { cancelled = true; };
    }, [content]);

    return (
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={`Poster preview for ${content.albumName || content.eventName}`}
        className={`${styles['canvas']} ${ready ? styles['canvas-ready'] : styles['canvas-loading']}`}
      />
    );
  },
);

A4Poster.displayName = 'A4Poster';

// ── Helpers ───────────────────────────────────────────────────────────────────

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * Strip the scheme for display — posters read better without `https://`,
 * and guests type it without one anyway. The QR still encodes the full URL.
 */
export function displayUrl(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

/** Greedy word wrap, ellipsising the final line when it runs out of room. */
export function wrapLines(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number,
): string[] {
  const trimmed = text.trim();
  if (!trimmed) return [];

  const words = trimmed.split(/\s+/);
  const lines: string[] = [];
  let current = '';

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (ctx.measureText(candidate).width <= maxWidth || !current) {
      current = candidate;
      continue;
    }
    lines.push(current);
    current = word;
    if (lines.length === maxLines) break;
  }

  if (lines.length < maxLines && current) lines.push(current);

  // Anything that didn't fit gets folded into an ellipsis on the last line.
  const consumed = lines.join(' ').split(/\s+/).length;
  if (consumed < words.length && lines.length) {
    lines[lines.length - 1] = fitText(ctx, `${lines[lines.length - 1]}…`, maxWidth);
  } else if (lines.length) {
    lines[lines.length - 1] = fitText(ctx, lines[lines.length - 1], maxWidth);
  }

  return lines;
}

/** Trim a single line with an ellipsis until it fits `maxWidth`. */
export function fitText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let out = text;
  while (out.length > 1 && ctx.measureText(`${out}…`).width > maxWidth) {
    out = out.slice(0, -1);
  }
  return `${out}…`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Multiply the alpha of an `rgba(...)` string, clamped to 1. Lets the pattern
 * "Strength" knob scale a palette ink without each pattern re-deriving colour.
 */
function withAlphaScale(rgba: string, factor: number): string {
  const m = /^rgba?\(([^)]+)\)$/.exec(rgba.trim());
  if (!m) return rgba;
  const parts = m[1].split(',').map((v) => v.trim());
  const alpha = parts.length > 3 ? parseFloat(parts[3]) : 1;
  const next = Math.min(1, Math.max(0, alpha * factor));
  return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${next})`;
}

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/** `letterSpacing` is not in every engine yet; skip it where unsupported. */
function setLetterSpacing(ctx: CanvasRenderingContext2D, px: number) {
  if ('letterSpacing' in ctx) {
    (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = `${px}px`;
  }
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number,
  w: number, h: number,
  r: number,
) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}
